import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { getPaymentMethodFromPolymorphicTransaction } from 'assets/js/pages/transactions/util';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import Amex from 'images/amex.svg?react';
import ApplePay from 'images/applepay.svg?react';
import Daf from 'images/daf.svg?react';
import Discover from 'images/discover.svg?react';
import Mastercard from 'images/mastercard.svg?react';
import Paypal from 'images/paypal.svg?react';
import Venmo from 'images/venmo.svg?react';
import Visa from 'images/visa.svg?react';
import { twMerge } from 'tailwind-merge';
import { PolymorphicTransaction, TransactionPaymentMethod, TransactionSubtype } from 'types/domain';

interface TransactionMethodProps {
    transactionId?: number;
}

const TransactionMethod = (props: TransactionMethodProps) => {
    const { transactionId } = props;
    const { data: transaction } = useCurrentTransactionOrById({ transactionId });

    return <TransactionMethodPresentational transaction={transaction} />;
};

interface TransactionMethodPresentationProps {
    transaction: PolymorphicTransaction;
}

const TransactionMethodPresentational = (props: TransactionMethodPresentationProps) => {
    const { transaction } = props;
    const { method, method_subtype, cc_last_four } =
        getPaymentMethodFromPolymorphicTransaction(transaction);

    const { transactionIcon, possibleTransactionText } = getPaymentIconInfo(
        method,
        method_subtype,
        cc_last_four,
    );

    return (
        <div className={'flex items-center gap-2'}>
            <PaymentIconContainer>{transactionIcon}</PaymentIconContainer>
            {possibleTransactionText && (
                <p className={'text-gray-600'}>{possibleTransactionText}</p>
            )}
        </div>
    );
};

interface PaymentMethodsPresentationalProps {
    payment: TransactionPaymentMethod;
}

const PaymentMethodsPresentational = (props: PaymentMethodsPresentationalProps) => {
    const { payment } = props;
    const { method, method_subtype, cc_last_four } = payment;

    const { transactionIcon, possibleTransactionText } = getPaymentIconInfo(
        method,
        method_subtype,
        cc_last_four,
    );

    return (
        <div className={'flex items-center gap-2'}>
            <PaymentIconContainer>{transactionIcon}</PaymentIconContainer>
            {possibleTransactionText && (
                <p className={'text-sm text-gray-600'}>{possibleTransactionText}</p>
            )}
        </div>
    );
};

interface PaymentIconContainerProps extends ComponentPropsWithoutRef<'div'> {}

const getPaymentIconInfo = (
    method: string | undefined,
    methodSubtype: TransactionSubtype | null | undefined,
    ccLastFour: string | null | undefined,
) => {
    let transactionIcon: ReactNode;
    let possibleTransactionText: string | undefined;

    switch (method) {
        case 'ach':
            transactionIcon = <PaymentIconText>Ach</PaymentIconText>;
            break;
        case 'cash':
            transactionIcon = <PaymentIconText>Cash</PaymentIconText>;
            break;
        case 'check':
            transactionIcon = <PaymentIconText>Check</PaymentIconText>;
            break;
        case 'digital_wallet':
            transactionIcon = <PaymentIconText>Digital Wallet</PaymentIconText>;
            break;
        case 'paypal':
            transactionIcon = <Paypal />;
            break;
        case 'venmo':
            transactionIcon = <Venmo />;
            break;
        case 'terminal':
            transactionIcon = <PaymentIconText>Terminal</PaymentIconText>;
            break;
        case 'donor_advised_fund':
            transactionIcon = <Daf />;
            possibleTransactionText = 'DAF';
            break;
        case 'card':
            if (!methodSubtype) {
                transactionIcon = <PaymentIconText>Card</PaymentIconText>;
            } else {
                transactionIcon = <PaymentProcessorIcon type={methodSubtype} />;

                if (methodSubtype !== 'applepay' && !!ccLastFour) {
                    possibleTransactionText = `Ending in ${ccLastFour}`;
                }
            }
            break;
        case 'property':
            transactionIcon = <PaymentIconText>Property</PaymentIconText>;
            break;
        case 'in-kind':
            transactionIcon = <PaymentIconText>In-kind</PaymentIconText>;
            break;
        case 'stock':
            transactionIcon = <PaymentIconText>Stock</PaymentIconText>;
            break;
        case 'other':
            transactionIcon = <PaymentIconText>Other</PaymentIconText>;
            break;
        case 'none':
        default:
            break;
    }

    return {
        transactionIcon,
        possibleTransactionText,
    };
};

const PaymentIconContainer = (props: PaymentIconContainerProps) => {
    const { className, ...rest } = props;

    return (
        <div
            className={twMerge(
                'flex min-h-6 min-w-[34px] items-center justify-center overflow-clip rounded-md border border-gray-200',
                className,
            )}
            {...rest}
        />
    );
};

interface PaymentIconTextProps extends ComponentPropsWithoutRef<'p'> {}
const PaymentIconText = (props: PaymentIconTextProps) => {
    const { className, ...rest } = props;

    return (
        <p className={twMerge('px-2 py-1 text-xs uppercase text-gray-600', className)} {...rest} />
    );
};

interface PaymentProcessorIconProps {
    type: TransactionSubtype;
}
const PaymentProcessorIcon = (props: PaymentProcessorIconProps) => {
    const { type } = props;

    switch (type) {
        case 'amex':
            return <Amex />;
        case 'applepay':
            return <ApplePay />;
        case 'discover':
            return <Discover />;
        case 'mastercard':
            return <Mastercard />;
        case 'visa':
            return <Visa />;
        default:
            return null;
    }
};

export {
    TransactionMethod,
    TransactionMethodPresentational,
    PaymentMethodsPresentational,
    PaymentProcessorIcon,
};
